import {
  AlertCircleIcon,
  Delete01Icon,
  Mail01Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  type ColDef,
  type ICellRendererParams,
  type RowClickedEvent
} from "ag-grid-community";
import { useLayout } from "providers/Layout/hooks";
import { useCallback, useEffect, useMemo, useState } from "react";
import { instance } from "api/apiBase";
import { Pagination } from "shared/components/Pagination";
import {
  Badge,
  BadgeSize,
  BadgeVariant,
  IconButton,
  IconButtonVariant,
  Show
} from "shared/components";
import { AgGrid } from "shared/components/AgGrid";
import { formatPhone } from "shared/utils";
import { toast } from "react-toastify";
import { Copy06 } from "untitledui-js-base";
import { getErrorMessage } from "utils/error";
import { handleCopy } from "utils/handleCopy";
import {
  REVIEW_ITEMS_PENDING_QUERY_KEY,
  type ReviewItem,
  type ReviewReason
} from "shared/app/LeadsTableView/slices/ReviewQueueTable/types";
import { fetchPendingReviewItems } from "shared/app/LeadsTableView/slices/ReviewQueueTable/useReviewQueue";

const REASON_CONFIG: Record<
  ReviewReason,
  { label: string; variant: BadgeVariant; icon: typeof AlertCircleIcon }
> = {
  no_property_match: {
    label: "No property match",
    variant: BadgeVariant.WARNING,
    icon: AlertCircleIcon
  },
  property_not_found: {
    label: "Unsure which property",
    variant: BadgeVariant.ERROR,
    icon: AlertCircleIcon
  },
  no_unit_match: {
    label: "Pick a unit",
    variant: BadgeVariant.WARNING,
    icon: AlertCircleIcon
  },
  no_contact_method: {
    label: "No contact info found",
    variant: BadgeVariant.ERROR,
    icon: Mail01Icon
  },
  incomplete_contact: {
    label: "Missing email or phone",
    variant: BadgeVariant.ERROR,
    icon: Mail01Icon
  }
};

const fullName = (item: ReviewItem) =>
  [item.first_name, item.last_name].filter(Boolean).join(" ").trim();

const contactLine = (item: ReviewItem) =>
  [item.email, item.phone ? formatPhone(item.phone) : null]
    .filter(Boolean)
    .join(" · ");

const formatReceivedAt = (iso: string) =>
  new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });

interface ReviewQueueTableProps {
  ingestAddress?: string;
  search?: string;
  onCountChange?: (count: number) => void;
  onReview?: (item: ReviewItem) => void;
}

export const ReviewQueueTable = ({
  ingestAddress,
  search = "",
  onCountChange,
  onReview
}: ReviewQueueTableProps) => {
  const queryClient = useQueryClient();
  const { sectionHeight } = useLayout();
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const { data, isLoading } = useQuery({
    queryKey: [...REVIEW_ITEMS_PENDING_QUERY_KEY, page, search],
    queryFn: () => fetchPendingReviewItems(page, search)
  });

  useEffect(() => {
    if (data) onCountChange?.(data.total_count);
  }, [data, onCountChange]);

  const { mutate: dismiss, isPending: isDismissing } = useMutation({
    mutationKey: ["LEAD_INGEST_REVIEW_ITEM_DISMISS"],
    mutationFn: (id: number) =>
      instance.post(
        `/portal/v1/boom_crm/lead_ingest_review_items/${id}/dismiss`
      ),
    onSuccess: () => {
      toast.success("Item dismissed");
      return queryClient.refetchQueries({
        queryKey: REVIEW_ITEMS_PENDING_QUERY_KEY
      });
    },
    onError: (error) => toast.error(getErrorMessage(error))
  });

  const items = data?.items ?? [];
  const totalCount = data?.total_count ?? 0;
  const pageCount = data?.page_count ?? 0;

  useEffect(() => {
    if (pageCount > 0 && page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const columns = useMemo<ColDef<ReviewItem>[]>(
    () => [
      {
        headerName: "Flagged for",
        colId: "reason",
        minWidth: 240,
        flex: 1,
        cellRenderer: (params: ICellRendererParams<ReviewItem>) => {
          const row = params.data;
          if (!row) return null;
          const reason = REASON_CONFIG[row.reason] ?? {
            label: row.reason,
            variant: BadgeVariant.BASE,
            icon: AlertCircleIcon
          };
          return (
            <div className="flex h-full items-center">
              <Badge
                variant={reason.variant}
                size={BadgeSize.SM}
                leftElement={<HugeiconsIcon icon={reason.icon} size={14} />}
                className="whitespace-nowrap"
              >
                {reason.label}
              </Badge>
            </div>
          );
        }
      },
      {
        headerName: "Extracted contact",
        colId: "contact",
        minWidth: 200,
        flex: 1.4,
        cellRenderer: (params: ICellRendererParams<ReviewItem>) => {
          const row = params.data;
          if (!row) return null;
          const name = fullName(row);
          const contact = contactLine(row);
          return (
            <div className="flex h-full min-w-0 flex-col justify-center">
              <p className="truncate text-sm font-bold text-content-1000">
                {name || (
                  <span className="font-normal italic text-content-300">
                    No name
                  </span>
                )}
              </p>
              <p className="truncate text-sm text-content-300">
                {contact || <span className="italic">Not found</span>}
              </p>
            </div>
          );
        }
      },
      {
        headerName: "Best-guess property",
        colId: "best_guess_property",
        minWidth: 200,
        flex: 1.4,
        cellRenderer: (params: ICellRendererParams<ReviewItem>) => {
          const row = params.data;
          if (!row) return null;
          return (
            <div className="flex h-full items-center">
              <p className="truncate text-sm text-content-700">
                {row.best_guess_property_name ?? (
                  <span className="italic text-content-300">No match</span>
                )}
              </p>
            </div>
          );
        }
      },
      {
        headerName: "Received via",
        colId: "received_via",
        minWidth: 140,
        flex: 1,
        cellRenderer: (params: ICellRendererParams<ReviewItem>) => {
          const row = params.data;
          if (!row) return null;
          return (
            <div className="flex h-full items-center">
              <p className="truncate text-sm text-content-300">
                {row.received_via ?? "—"}
              </p>
            </div>
          );
        }
      },
      {
        headerName: "Received",
        colId: "created_at",
        width: 170,
        minWidth: 170,
        cellRenderer: (params: ICellRendererParams<ReviewItem>) => {
          const row = params.data;
          if (!row) return null;
          return (
            <div className="flex h-full items-center">
              <p className="whitespace-nowrap text-sm text-content-300">
                {formatReceivedAt(row.created_at)}
              </p>
            </div>
          );
        }
      },
      {
        headerName: "",
        colId: "actions",
        width: 72,
        minWidth: 72,
        cellRenderer: (params: ICellRendererParams<ReviewItem>) => {
          const row = params.data;
          if (!row) return null;
          return (
            <div className="flex h-full items-center justify-end">
              <IconButton
                HugeIcon={Delete01Icon}
                variant={IconButtonVariant.TEXT}
                disabled={isDismissing}
                onClick={(e) => {
                  // Row click opens the review drawer; stop the trash click from
                  // bubbling up to the row so dismiss doesn't also open it.
                  e.stopPropagation();
                  dismiss(row.id);
                }}
              />
            </div>
          );
        }
      }
    ],
    [dismiss, isDismissing]
  );

  const getRowId = useCallback(
    (params: { data: ReviewItem }) => String(params.data.id),
    []
  );

  const onRowClicked = useCallback(
    (event: RowClickedEvent<ReviewItem>) => {
      if (event.data) onReview?.(event.data);
    },
    [onReview]
  );

  return (
    <div className="flex w-full flex-col overflow-hidden rounded-lg border border-alpha-100 bg-background-1000">
      <p className="px-6 py-4 text-sm text-content-300">
        Emails forwarded to{" "}
        <span className="break-all font-bold text-content-1000">
          {ingestAddress ?? "your lead ingestion address"}
        </span>
        <Show if={Boolean(ingestAddress)}>
          <IconButton
            Icon={Copy06}
            variant={IconButtonVariant.TEXT}
            className="!inline-flex !h-5 !w-5 translate-y-1 !stroke-content-200"
            onClick={() =>
              handleCopy(String(ingestAddress), "Email address copied")
            }
          />
        </Show>{" "}
        that our AI couldn&apos;t turn into a lead automatically. Assign a
        property or fix contact info to create the lead, or dismiss anything
        that isn&apos;t a real inquiry.
      </p>

      <AgGrid<ReviewItem>
        columns={columns}
        rows={items}
        loading={isLoading}
        rowHeight={72}
        headerHeight={44}
        perPageRows={20}
        viewPortHeight={sectionHeight}
        viewPortHeightOffset={300}
        rowClass="cursor-pointer"
        onRowClicked={onRowClicked}
        wrapperClassName="border-none rounded-none"
        themeOverrides={{ "--ag-cell-horizontal-padding": "24px" }}
        getRowId={getRowId}
      />

      <Show if={totalCount > 0}>
        <div className="flex items-center justify-between border-t border-alpha-40 px-6 py-4">
          <p className="whitespace-nowrap font-medium text-content-1000 text-base">
            Total items: {totalCount}
          </p>
          <Show if={pageCount > 1}>
            <div className="ml-auto flex">
              <Pagination
                className="justify-end"
                total={pageCount}
                current={page}
                onChange={setPage}
              />
            </div>
          </Show>
        </div>
      </Show>
    </div>
  );
};
